import React, { useState } from "react";
import { motion } from "framer-motion";
import { Calculator, ArrowRightLeft, Weight, Ruler, Factory, Info } from "lucide-react";
import { IMAGES } from "@/assets/images";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { fadeInUp, staggerContainer, staggerItem } from "@/lib/motion";

export default function ProfileGuideEn() {
  const [inchValue, setInchValue] = useState("");
  const [mmValue, setMmValue] = useState("");
  const [length, setLength] = useState("");
  const [width, setWidth] = useState("");
  const [thickness, setThickness] = useState("");
  const [kgValue, setKgValue] = useState("");
  const [lbsValue, setLbsValue] = useState("");

  // Conversion functions
  const convertInchToMm = (inch: number) => (inch * 25.4).toFixed(2);
  const convertMmToInch = (mm: number) => (mm / 25.4).toFixed(4);
  const convertKgToLbs = (kg: number) => (kg * 2.20462).toFixed(3);
  const convertLbsToKg = (lbs: number) => (lbs / 2.20462).toFixed(3);

  const handleInchChange = (value: string) => {
    setInchValue(value);
    if (value && !isNaN(Number(value))) {
      setMmValue(convertInchToMm(Number(value)));
    } else {
      setMmValue("");
    }
  };

  const handleMmChange = (value: string) => {
    setMmValue(value);
    if (value && !isNaN(Number(value))) {
      setInchValue(convertMmToInch(Number(value)));
    } else {
      setInchValue("");
    }
  };

  const handleKgChange = (value: string) => {
    setKgValue(value);
    if (value && !isNaN(Number(value))) {
      setLbsValue(convertKgToLbs(Number(value)));
    } else {
      setLbsValue("");
    }
  };

  const handleLbsChange = (value: string) => {
    setLbsValue(value);
    if (value && !isNaN(Number(value))) {
      setKgValue(convertLbsToKg(Number(value)));
    } else {
      setKgValue("");
    }
  };

  // Weight calculation (Aluminum density: 2.7 g/cm³)
  const calculateWeight = () => {
    if (length && width && thickness) {
      const volume = Number(length) * Number(width) * Number(thickness); // mm³
      const volumeCm3 = volume / 1000; // cm³
      const weight = volumeCm3 * 2.7; // gram
      return weight.toFixed(2);
    }
    return "0";
  };

  // Common profile sizes
  const commonSizes = [
    { inch: "4\"", mm: "101.6", description: "Small and medium-scale profiles" },
    { inch: "5\"", mm: "127.0", description: "Standard architectural profiles" },
    { inch: "6\"", mm: "152.4", description: "Industrial profiles" },
    { inch: "7\"", mm: "177.8", description: "Large-scale structural components" },
    { inch: "8\"", mm: "203.2", description: "Massive cross-section industrial profiles" },
  ];

  return (
    <div className="flex flex-col w-full">
      {/* Hero Section */}
      <section className="relative py-24 bg-primary text-primary-foreground overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img 
            src={IMAGES.FACTORY_FLOOR_1} 
            alt="Profile Guide Background" 
            className="w-full h-full object-cover"
          />
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-4xl mx-auto"
          >
            <div className="inline-flex items-center gap-2 bg-white/10 px-4 py-2 rounded-full mb-6">
              <Calculator className="w-5 h-5" />
              <span className="text-sm font-medium">Technical Calculations</span>
            </div>
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Profile Guide
            </h1>
            <p className="text-xl text-primary-foreground/80 leading-relaxed">
              Aluminum profile calculations, dimension converters and production line profile catalog. 
              Comprehensive tool set for your technical calculations.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Calculators Section */}
      <section className="py-24 bg-background">
        <div className="container mx-auto px-4">
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <motion.div variants={staggerItem} className="text-center mb-16">
              <h2 className="text-3xl font-bold mb-4">Calculators</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Practical tools for profile dimensions and weight calculations
              </p>
            </motion.div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
              {/* Inch ↔ Millimeter Converter */}
              <motion.div variants={staggerItem}>
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <ArrowRightLeft className="w-5 h-5" />
                      Inch ↔ Millimeter Converter
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="inch">Inch</Label>
                        <Input
                          id="inch"
                          type="number"
                          placeholder="e.g. 4"
                          value={inchValue}
                          onChange={(e) => handleInchChange(e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="mm">Millimeter (mm)</Label>
                        <Input
                          id="mm"
                          type="number"
                          placeholder="e.g. 101.6"
                          value={mmValue}
                          onChange={(e) => handleMmChange(e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="mt-4 p-3 bg-muted rounded-lg">
                      <p className="text-sm text-muted-foreground">
                        <Info className="w-4 h-4 inline mr-2" />
                        1 inch = 25.4 millimeters
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>

              {/* KG ↔ LBS Converter */}
              <motion.div variants={staggerItem}>
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <ArrowRightLeft className="w-5 h-5" />
                      KG ↔ LBS Converter
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="kg">Kilogram (KG)</Label>
                        <Input
                          id="kg"
                          type="number"
                          placeholder="e.g. 10"
                          value={kgValue}
                          onChange={(e) => handleKgChange(e.target.value)}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="lbs">Pound (LBS)</Label>
                        <Input
                          id="lbs"
                          type="number"
                          placeholder="e.g. 22.046"
                          value={lbsValue}
                          onChange={(e) => handleLbsChange(e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="mt-4 p-3 bg-muted rounded-lg">
                      <p className="text-sm text-muted-foreground">
                        <Info className="w-4 h-4 inline mr-2" />
                        1 KG = 2.20462 LBS
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            </div>

            {/* Weight Calculator */}
            <motion.div variants={staggerItem} className="mb-8">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Weight className="w-5 h-5" />
                    Aluminum Profile Weight Calculator
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                    <div className="space-y-2">
                      <Label htmlFor="length">Length (mm)</Label>
                      <Input
                        id="length"
                        type="number"
                        placeholder="e.g. 6000"
                        value={length}
                        onChange={(e) => setLength(e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="width">Width (mm)</Label>
                      <Input
                        id="width"
                        type="number"
                        placeholder="e.g. 50"
                        value={width}
                        onChange={(e) => setWidth(e.target.value)}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="thickness">Thickness (mm)</Label>
                      <Input
                        id="thickness"
                        type="number"
                        placeholder="e.g. 2"
                        value={thickness}
                        onChange={(e) => setThickness(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="p-4 bg-primary/10 rounded-lg">
                    <div className="flex items-center justify-between">
                      <span className="font-medium">Estimated Weight:</span>
                      <span className="text-2xl font-bold text-primary">
                        {calculateWeight()} grams
                      </span>
                    </div>
                    <p className="text-sm text-muted-foreground mt-2">
                      * Aluminum density calculated as 2.7 g/cm³
                    </p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>

          </motion.div>
        </div>
      </section>

      {/* Production Lines Section */}
      <section className="py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <motion.div variants={staggerItem} className="text-center mb-16">
              <h2 className="text-3xl font-bold mb-4">Production Line Profiles</h2>
              <p className="text-muted-foreground max-w-2xl mx-auto">
                Profile varieties and technical specifications produced in our 4" - 8" extrusion lines
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {commonSizes.map((size, idx) => (
                <motion.div key={idx} variants={staggerItem}>
                  <Card className="h-full hover:shadow-lg transition-shadow">
                    <CardHeader>
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-primary/10">
                          <Factory className="w-6 h-6 text-primary" />
                        </div>
                        <div>
                          <CardTitle className="text-lg">{size.inch} Extrusion Line</CardTitle>
                          <p className="text-sm text-muted-foreground">{size.mm} mm</p>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-muted-foreground mb-4">{size.description}</p>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span>Annual Capacity:</span>
                          <span className="font-mono">{(2400 + idx * 1800)} Tons</span>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* US EXTRUSIONS STANDARDS Section */}
      <section className="py-24 bg-background">
        <div className="container mx-auto px-4">
          <motion.div
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            <motion.div variants={staggerItem} className="text-center mb-16">
              <div className="flex items-center justify-center gap-4 mb-6">
            <h2 className="text-3xl font-bold flex items-center gap-3">
              <span className="text-4xl">🇺🇸</span>
              EXTRUSIONS STANDARDS
            </h2>
                <Button variant="outline" size="sm" asChild>
                  <a href="/catalogue.pdf" download="US-Extrusions-Standards.pdf" className="flex items-center gap-2">
                    <Factory className="w-4 h-4" />
                    Download PDF
                  </a>
                </Button>
              </div>
              <div className="max-w-3xl mx-auto space-y-3">
                <p className="text-muted-foreground">
                  Aluminum profile catalog and technical specifications compliant with American standards
                </p>
                <div className="space-y-2">
                  <div className="inline-flex items-center gap-2 bg-blue-50 text-blue-700 px-4 py-2 rounded-lg text-sm">
                    <Info className="w-4 h-4" />
                    <span>This page shows selected examples from the catalog. Download the PDF for the complete catalog.</span>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div variants={staggerItem}>
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Factory className="w-5 h-5" />
                    Standard Shapes Catalogue
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="w-full max-h-[800px] overflow-y-auto border rounded-lg bg-white">
                    <div className="p-6">
                      <div className="text-center mb-8">
                        <h3 className="text-2xl font-bold mb-2">US EXTRUSIONS STANDARDS</h3>
                        <p className="text-muted-foreground">Standard Shapes Catalogue</p>
                      </div>
                      
                      {/* ANGLES - Equal Leg - Architectural */}
                      <div className="mb-8">
                        <h4 className="text-lg font-semibold mb-4 text-primary">ANGLES - Equal Leg - Architectural</h4>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm border-collapse">
                            <thead>
                              <tr className="bg-muted/50">
                                <th className="border p-2 text-center">DRAWING</th>
                                <th className="border p-2 text-left">PART NUMBER</th>
                                <th className="border p-2 text-center">E</th>
                                <th className="border p-2 text-center">A</th>
                                <th className="border p-2 text-center">T</th>
                                <th className="border p-2 text-center">LENGTH</th>
                                <th className="border p-2 text-center">FINISH</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <td rowSpan={3} className="border p-2 text-center align-middle">
                                  <img src="/images/cropped_image_2_1770648435542855986.jpg" alt="Angle Profile" className="w-16 h-12 object-contain mx-auto" />
                                </td>
                                <td className="border p-2 font-mono">IE 1207</td>
                                <td className="border p-2 text-center">5/8"</td>
                                <td className="border p-2 text-center">5/8"</td>
                                <td className="border p-2 text-center">1/16"</td>
                                <td className="border p-2 text-center">12'</td>
                                <td className="border p-2 text-center">AN</td>
                              </tr>
                              <tr>
                                <td className="border p-2 font-mono">IE 1208</td>
                                <td className="border p-2 text-center">13/16"</td>
                                <td className="border p-2 text-center">13/16"</td>
                                <td className="border p-2 text-center">0.060"</td>
                                <td className="border p-2 text-center">21'</td>
                                <td className="border p-2 text-center">AN</td>
                              </tr>
                              <tr>
                                <td className="border p-2 font-mono">IE 1209</td>
                                <td className="border p-2 text-center">1/2"</td>
                                <td className="border p-2 text-center">1/2"</td>
                                <td className="border p-2 text-center">1/16"</td>
                                <td className="border p-2 text-center">8', 12', 21'</td>
                                <td className="border p-2 text-center">AN, BC, BK, SB, SS, ML</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                      
                      {/* Finish Codes */}
                      <div className="mt-8 p-4 bg-muted/30 rounded-lg">
                        <h5 className="font-semibold mb-2">FINISH CODES:</h5>
                        <div className="text-sm text-muted-foreground grid grid-cols-2 md:grid-cols-4 gap-2">
                          <span><strong>AN:</strong> Anodized</span>
                          <span><strong>BC:</strong> Brite Clear</span>
                          <span><strong>BK:</strong> Brite Black</span>
                          <span><strong>BR:</strong> Brown</span>
                          <span><strong>BS:</strong> Brass</span>
                          <span><strong>ML:</strong> Mill</span>
                          <span><strong>SB:</strong> Satin Black</span>
                          <span><strong>SS:</strong> Stainless Steel Brushed</span>
                        </div>
                        <p className="text-xs text-muted-foreground mt-2">
                          * Minimum order quantities may apply on 20ft, 21ft & 24ft material.
                        </p>
                      </div>
                      
                      <div className="mt-6 text-center">
                        <p className="text-sm text-muted-foreground">
                          This catalog is prepared according to US Extrusions standards.
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}